import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Lock, Clock, KeyRound } from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useActivationKeys } from '@/features/solution-browser/hooks/useActivationKey'
import { useTestSeries } from '../hooks'
import { buildSeriesAccessMap } from '../utils/seriesAccess'
import ActivateSeriesDialog from './ActivateSeriesDialog'
import type { TestSeriesResponse } from '../types'

/**
 * Renders children only if the user holds a REDEEMED activation key for the
 * :seriesCode route param; otherwise offers activation in place.
 */
export default function SeriesAccessGuard({ children }: { children: React.ReactNode }) {
  const { seriesCode = '' } = useParams<{ seriesCode: string }>()
  const userId = useAuthStore((s) => s.userId) ?? undefined
  const [activatingSeries, setActivatingSeries] = useState<TestSeriesResponse | null>(null)

  const { data: keys, isPending, isError, refetch } = useActivationKeys(userId)
  const access = useMemo(
    () => (keys ? buildSeriesAccessMap(keys).get(seriesCode) ?? 'NONE' : null),
    [keys, seriesCode]
  )

  // Only needed for the locked screen's title and the activation dialog
  const { data: seriesData } = useTestSeries(access === 'ACTIVE' ? '' : seriesCode)
  const series = seriesData?.data

  if (access === 'ACTIVE') {
    return <>{children}</>
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="mb-4 text-red-800">We couldn't verify your access to this test series.</p>
        <button
          onClick={() => refetch()}
          className="rounded bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    )
  }

  const isExpired = access === 'EXPIRED'

  return (
    <div>
      <Link
        to="/test-series"
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        All test series
      </Link>

      <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm sm:p-8">
        <div className="mb-4 flex justify-center">
          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${isExpired ? 'bg-orange-50' : 'bg-gray-100'}`}>
            {isExpired ? <Clock className="h-7 w-7 text-orange-600" /> : <Lock className="h-7 w-7 text-gray-500" />}
          </div>
        </div>
        <h1 className="mb-2 text-xl font-bold text-gray-900">{series?.seriesName ?? 'Test Series'}</h1>
        <p className="mb-6 text-sm text-gray-600">
          {isExpired
            ? 'Your access to this test series has expired. Enter a new activation key to continue.'
            : 'This test series is locked. Enter the activation key from your book or email to unlock it.'}
        </p>
        <button
          onClick={() => series && setActivatingSeries(series)}
          disabled={!series || !userId}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <KeyRound className="h-4 w-4" />
          Activate
        </button>
      </div>

      {userId && (
        <ActivateSeriesDialog
          userId={userId}
          series={activatingSeries}
          onClose={() => setActivatingSeries(null)}
          // Keys are refetched by the redeem mutation; the guard re-evaluates on its own
          onSuccess={() => setActivatingSeries(null)}
        />
      )}
    </div>
  )
}
